import React, { useState, useEffect } from 'react';
import {
  Flex,
  Text,
  Button,
  useToast,
  FormControl,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { EditIcon, CloseIcon } from '@chakra-ui/icons';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import Card from 'components/card/Card';
import { useAuth } from 'auth-context/auth.context';
import UserApi from 'api/user';
import { AdminUserFormValidationSchema } from 'formValidations/adminUserFormValidations';
import InputField from 'components/formComponents/inputField';
import SelectField from 'components/formComponents/selectField';
import { usePermissions } from 'contexts/PermissionContext';
import { Modules } from 'config/constant';
import NoPermissionsMessage from 'components/nopermission/NoPermissions';
import { isValidUUID } from 'utils/fetchUtils';
import PageError from 'components/nopermission/PageError';

export default function About(props) {
  const withId = props;
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const { user } = useAuth();
  const [disabled, setDisabled] = useState(true);
  const [inpDisable, setInpDisable] = useState(true);
  const [adminUserData, setAdminUserData] = useState({});
  const enable = () => setDisabled(!disabled);
  const disable = () => setDisabled(true);
  const { getPermissionsForModule } = usePermissions();
  const [permissions, setPermissions] = useState(null);
  const [customerPermission, setCustomerPermissions] = useState(null);
  const [fetchAdminDataError, setFetchAdminDataError] = useState(false);

  const adminUserId = IdFromUrl(window.location.href);
  function IdFromUrl(url) {
    // Split the URL by slashes
    const parts = url.split('/');

    // The last part of the URL is the ID
    const id = parts[parts.length - 1];

    return id;
  }

  useEffect(() => {
    const permissions = getPermissionsForModule([Modules.AdminUser]);
    const customperm = getPermissionsForModule([Modules.Customer]);
    setCustomerPermissions(customperm);
    setPermissions(permissions);
  }, []);

  const FetchAdminUserData = async () => {
    if (withId && !isValidUUID(adminUserId)) {
      setFetchAdminDataError(true);
      return;
    }
    try {
      setLoading(true);
      let response = await UserApi.GetById(adminUserId);
      response = response.data;
      //   console.log('Admin User =', response);
      setLoading(false);
      if (response.status_code === 200) {
        setFetchAdminDataError(false);
        setAdminUserData(response?.data?.customer);
      } else {
        setFetchAdminDataError(true);
        setAdminUserData({});
      }
    } catch (err) {
      setFetchAdminDataError(true);
      console.log(err);
    }
  };

  useEffect(() => {
    if (customerPermission?.read) {
      FetchAdminUserData();
    }
  }, [customerPermission]);

  const initialValues = {
    name: adminUserData.name || '',
    email: adminUserData.email || '',
    phoneNumber: adminUserData.phone_number || '',
    countryCode: adminUserData.country_code || '',
    status: adminUserData.status || '',
    userType: adminUserData.user_type || '',
  };

  const handleUpdate = async (values, { setSubmitting }) => {
    try {
      let response = await UserApi.Update({
        userId: adminUserId,
        ...values,
      });
      response = response.data;
      if (response.status_code === 200) {
        toast({
          title: response.message || 'Successfully Updated',
          status: 'success',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      } else if (response.status_code !== 200) {
        toast({
          title: response.message || 'Error Updating',
          status: 'error',
          duration: 5000,
          isClosable: true,
          position: 'top',
        });
      }
    } catch (error) {
      toast({
        title: 'Unable to Update',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top',
      });
      console.log(error);
    } finally {
      setSubmitting(false);
      setDisabled(true);
    }
  };
  if (!customerPermission?.read) {
    return <NoPermissionsMessage module={Modules.Customer} />;
  }
  return loading ? (
    <Center
      mt={20}
      width='100%'>
      <Spinner size='xl' />
    </Center>
  ) : fetchAdminDataError ? (
    <PageError />
  ) : (
    <>
      <Flex
        alignItems='center'
        justifyContent='space-between'
        mt={10}
        mb={10}>
        <Text
          fontSize='3xl'
          fontWeight='400'>
          About - {adminUserData.name}
        </Text>
        {disabled ? (
          <EditIcon
            ml='5'
            fontSize='large'
            onClick={enable}
            cursor='pointer'
          />
        ) : (
          <CloseIcon
            ml='5'
            fontSize='large'
            onClick={disable}
            cursor='pointer'
          />
        )}
      </Flex>

      <Formik
        initialValues={initialValues}
        validationSchema={AdminUserFormValidationSchema}
        onSubmit={handleUpdate}
        enableReinitialize>
        {({ setFieldValue, isSubmitting }) => (
          <Form noValidate>
            <Flex direction='column'>
              <Card
                bgColor='gray.200'
                mt={12}>
                <InputField
                  label='Admin Name'
                  required={!disabled && true}
                  name='name'
                  type='text'
                  variant='auth'
                  bgColor={disabled ? 'gray.200' : ''}
                  disabled={disabled}
                />
                <Flex
                  direction={{ base: 'column', md: 'column', lg: 'row' }}
                  justifyContent='space-between'
                  gap={2}>
                  <InputField
                    label='E-Mail Address'
                    required={!disabled && true}
                    name='email'
                    type='email'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <InputField
                    label='Phone Number'
                    required={!disabled && true}
                    name='phoneNumber'
                    type='text'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    disabled={disabled}
                  />
                  <SelectField
                    label='Status'
                    name='status'
                    variant='auth'
                    bgColor={disabled ? 'gray.200' : ''}
                    options={[
                      { value: 'ACTIVE', label: 'Active' },
                      { value: 'INACTIVE', label: 'Inactive' },
                      { value: 'UNVERIFIED', label: 'Unverified' },
                      { value: 'BLOCKED', label: 'Blocked' },
                    ]}
                    disabled={disabled}
                  />
                </Flex>
              </Card>

              {!disabled && (
                <Flex>
                  <Button
                    mt={20}
                    colorScheme='blue'
                    type='submit'
                    disabled={isSubmitting}>
                    Update Admin
                  </Button>
                </Flex>
              )}
            </Flex>
          </Form>
        )}
      </Formik>
    </>
  );
}
